import ArrayStore from 'bz-stores/mobx/array-store';
import useComputed from 'bz-utilities/hooks/computed';
import createContext from 'bz-utilities/hooks/context-v2';
import useUpdateEffect from 'bz-utilities/hooks/update-effect';
import { action, runInAction } from 'mobx';
import { observer, Provider } from 'mobx-react';
import React, { useEffect } from 'react';
import type { FormTemplatesResponse } from 'tm3-core/api.dtos';
import {
  ArchiveFormTemplateRequest,
  ClinicalMyTemplatesCloneRequest,
  DeleteFormTemplateRequest,
  FormTemplatesRequest
} from 'tm3-core/api.dtos';

export enum ETemplateType {
  Assessment = 'A',
  ClassNotes = 'K',
  ClinicalForm = 'F',
  ClinicalPSFS = 'L',
  Consultation = 'T',
  DeletedClinicalForm = 'Y',
  DeletedClinicalPSFS = 'Q',
  DeletedRegistration = 'W',
  DeletedSurvey = 'Z',
  Discharge = 'D',
  Modalities = 'M',
  Referral = 'R',
  Registration = 'E',
  Snippet = 'S',
  Survey = 'V'
}

export const [BzTemplatesStoreContext, useBzTemplatesStore] =
  createContext<BzTemplatesStore>('BzTemplatesStore');

export interface IProps extends React.PropsWithChildren {
  req?: Partial<FormTemplatesRequest>;
  skipLoad?: boolean;
}

export const useForms = (type: ETemplateType): FormTemplatesResponse[] => {
  const store = useBzTemplatesStore();

  return useComputed(() => {
    return store.data.filter((t) => {
      return t.type === type;
    });
  }, [store, type]);
};

export const BzTemplatesStoreProvider: React.FC<IProps> = observer((props) => {
  const { children, req, skipLoad } = props;

  const [store] = React.useState(() => new BzTemplatesStore());

  useEffect(() => {
    if (!skipLoad) {
      store.load(req);
    }
  }, [req, store, skipLoad]);

  return (
    <BzTemplatesStoreContext.Provider value={store}>
      <Provider formTemplateStore={store} templatesStore={store}>
        {children}
      </Provider>
    </BzTemplatesStoreContext.Provider>
  );
});

BzTemplatesStoreProvider.displayName = 'BzTemplatesStoreProvider';

class BzTemplatesStore extends ArrayStore<FormTemplatesResponse, unknown> {
  latest: Partial<FormTemplatesRequest> = {};

  @action.bound
  async load(req?: Partial<FormTemplatesRequest>): Promise<void> {
    const toSend = req ?? this.latest;

    this.loading = true;
    this.latest = toSend;

    try {
      const { results } = await this.client.post(new FormTemplatesRequest(toSend));
      this.onLoad(results);
    } catch (e) {
      this.onError(e);
      throw e;
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  @action.bound
  async cloneForm(id: number): Promise<void> {
    const req = new ClinicalMyTemplatesCloneRequest({ id });
    await this.client.get(req);
    await this.load();
  }

  @action.bound
  async archiveForm(id: number): Promise<void> {
    const req = new ArchiveFormTemplateRequest({ id });
    await this.client.post(req);
    await this.load();
  }

  @action.bound
  async removeForm(id: number): Promise<void> {
    const req = new DeleteFormTemplateRequest({ id });
    await this.client.post(req);
    await this.load();
  }
}

export default BzTemplatesStore;
